<script setup>
import {
  computed,
  useRoute,
  useSanityQuery,
  showError,
  useScrollTopOnMounted,
  useFetchOtherNews,
  useHead,
  useImageSource,
  ref,
  nextTick,
} from '#imports';
import { getQuery as getNewsItemQuery } from '~/groq-queries/news-item';
import { getPostMetaTags } from '~/utils/get-post-meta-tags';
import { getQuery as getDisclaimerQuery } from '~/groq-queries/disclaimer';

useScrollTopOnMounted();

const route = useRoute();
const { slug } = route.params;
const { previewId } = route.query;

const isPreview = computed(() => !!previewId);
const sanityClient = computed(() => (previewId ? 'previewClient' : 'default'));

const newsItemQuery = getNewsItemQuery({
  slug,
  isDraft: !!previewId,
  previewId,
});

const disclaimerQuery = getDisclaimerQuery();
const { data: newsItem, refresh } = await useSanityQuery(
  newsItemQuery,
  {},
  {
    client: sanityClient.value,
  }
);

const isDataEmpty = computed(() => !newsItem.value?._id);
if (isDataEmpty.value)
  showError({ statusCode: 404, message: 'Страница не найдена' });

const { data: otherNews } = await useFetchOtherNews({ slug, newsItem });

const { data: disclaimerSitewide } = await useSanityQuery(
  disclaimerQuery,
  {},
  {
    client: sanityClient.value,
  }
);

const renderComponent = ref(true);
const pageTitle = computed(
  () => `${newsItem.value.seoTitle || newsItem.value.title} — DOXA`
);
const ogImageUrl = computed(() =>
  useImageSource({ image: newsItem.value.ogImage })
);

useHead({
  title: pageTitle,
  meta: isDataEmpty.value
    ? []
    : getPostMetaTags({
        ogImageUrl: ogImageUrl.value,
        ogType: 'article',
        author: 'DOXA team',
        path: route.fullPath,
        ...newsItem.value,
      }),
  script: [
    {
      type: 'application/ld+json',
      key: 'ld-article',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: newsItem.value.title,
        description: newsItem.value.description || newsItem.value.title,
        datePublished: newsItem.value.publishedAt,
        dateModified: newsItem.value._updatedAt || newsItem.value.publishedAt,
        author: {
          '@type': 'Organization',
          name: 'DOXA',
        },
        publisher: {
          '@type': 'Organization',
          name: 'DOXA',
          url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app',
          logo: {
            '@type': 'ImageObject',
            url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app/logo.png',
          },
        },
        image: ogImageUrl.value,
        url: `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/news/${newsItem.value.slug.current}`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/news/${newsItem.value.slug.current}`,
        },
      }),
    },
  ],
});

const breadcrumbItems = computed(() => {
  const { tags, title } = newsItem.value;
  const firstTag = tags && tags.length > 0 ? tags[0] : null;

  const breadcrumbs = [
    { text: 'Главная', href: '/' },
    { text: 'Новости', href: '/news' },
  ];

  if (firstTag && firstTag.name) {
    breadcrumbs.push({
      text: firstTag.name,
      href: `/news?tag=${firstTag.name}`,
    });
  }

  breadcrumbs.push({ text: title });

  return breadcrumbs;
});

const forceRerender = async () => {
  // Remove MyComponent from the DOM
  renderComponent.value = false;
  // Wait for the change to get flushed to the DOM
  await nextTick();
  await refresh();
  // Add the component back in
  renderComponent.value = true;
};
</script>

<template>
  <div v-if="!isDataEmpty" class="news-page">
    <LayoutDefaultPage
      v-if="!isDataEmpty && renderComponent"
      is-animated-header
      is-header-shown
      :breadcrumb-items="breadcrumbItems"
    >
      <template v-if="isPreview" #progress-bar>
        <PreviewBar @click="forceRerender" />
      </template>
      <DisclaimerBlock
        v-if="disclaimerSitewide.isShowDisclaimer"
        :disclaimer="disclaimerSitewide"
      />
      <GoogleSourceBanner />
      <NewsItemHeader :news-item="newsItem" />
      <ArticleContent :content="newsItem" class="news-page__article-content" />
      <div class="news-page__other-news content-container">
        <MainNewsBlock
          :news="otherNews"
          title="Другие новости"
          white-background
        />
      </div>
    </LayoutDefaultPage>
  </div>
</template>

<style lang="postcss" scoped>
.news-page {
  position: relative;

  &__other-news {
    margin-bottom: 100px;
  }

  @media (--small-vp) {
    &__article-content {
      padding-bottom: 0;
    }
  }
}
</style>
